import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { LocalStorageService } from "ngx-store";
import { BehaviorSubject, forkJoin, Observable, of } from "rxjs";
import { concatMap, first, map, pluck, tap } from "rxjs/operators";
import { Connote, ConnoteSite } from "../model/dispatch.model";
import { Page } from "../model/pagination";
import { Url } from "../model/users.model";

/**
 * Describe what kind of connotes to retrieve.
 */
export interface FetchOptions {
  /**
   * Related models to include.
   */
  include?: string[];

  /**
   * Number of resources to fit per page.
   *
   * Setting this to -1 will fetch all resources into one page.
   */
  per_page?: number;

  /**
   * Filter fetched results.
   */
  filters?: Map<string, (string | number)[]>;
}

@Injectable({
  providedIn: 'root'
})
export class ConnoteService {
  private domain: Url = this.storage.get('url');

  public readonly endpoint = `${this.domain.rest}/tms/connotes`;

  private connotes: Observable<Connote>[] = [];

  private connoteList = new BehaviorSubject<Connote[]>([]);

  public connoteList$ = this.connoteList.asObservable();

  public constructor(
    private storage: LocalStorageService,
    private client : HttpClient
  ) { }

  /**
   * Get the cached list of connotes if available, or request for the list from
   * the server, and update cache.
   * @param options Options.
   * @returns Cold observable of the connote list.
   */
  public getConnotes(options?: FetchOptions) {
    if(this.connotes.length > 0) {
      return this.connoteList$.pipe(first());
    }

    return this.fetchConnotes(options)
      .pipe(
        concatMap((fetchedConnotes) => {
          if(!fetchedConnotes.length || !this.connotes.length) {
            this.connotes = fetchedConnotes.map((connote) => of(connote));
            return of(this.connotes);
          }

          return forkJoin(this.connotes)
            .pipe(
              map((cachedConnotes) => {
                fetchedConnotes.forEach((fetched) => {
                  const cacheToUpdate = cachedConnotes.findIndex((cached) => cached.id === fetched.id);

                  cacheToUpdate === -1
                    ? this.connotes.push(of(fetched))
                    : this.connotes.splice(cacheToUpdate, 1, of(fetched));

                });

                return this.connotes;
              })
            );
        }),
        concatMap((connotes) => forkJoin(connotes)),
        tap((connotes) => this.connoteList.next(connotes))
      );
  }

  /**
   * Retrieve the list of connotes from the server.
   * @param options Fetch Options.
   * @returns Cold observable of the fetched list.
   */
  public fetchConnotes(options?: FetchOptions) {
    const params = toHttpParams(options);

    return this.client
      .get<Page<Connote>>(this.endpoint, { params })
      .pipe(pluck('data'));
  }

  public getConnote(id: Connote['id']) {
    return forkJoin(this.connotes)
      .pipe(
        concatMap((cachedConnotes) => {
          const cached = cachedConnotes.find((cached) => cached.id === id);

          if(cached) {
            return of(cached);
          }

          const options: FetchOptions = {
            filters: new Map([
              ['id', [id]]
            ])
          };

          return this.getConnotes(options)
            .pipe(
              map((connotes) => connotes?.[0])
            );
        })
      );
  }

  public getConnoteSite(id: ConnoteSite['id']) {
    return forkJoin(this.connotes)
      .pipe(
        concatMap((connotes) => {
          const filter: FetchOptions = {
            include: ['sites'],
            filters: new Map([
              ['sites.id', [id]]
            ])
          };

          let cached: ConnoteSite | null = null;
          connotes.some((cachedConnote) => {
            cached = cachedConnote.sites.find((cachedSite) => cachedSite.id === id);
            return cached;
          });

          return cached
            ? of(cached)
            : this.getConnotes(filter).pipe(map((fetched) => fetched[0]
                ?.sites
                .find((fetchedSite) => fetchedSite.id === id) ?? null)
              );
        })
      );
  }
}

/**
 * Construct the http parameters for the given fetch options.
 * @param options Fetch options.
 * @returns Http parameters.
 */
function toHttpParams(options?: FetchOptions) {
  const fromObject: Record<string, string> = {};

  if(options?.include?.length) {
    fromObject['include'] = options.include.join();
  }

  if(options?.per_page) {
    fromObject['per_page'] = `${options.per_page}`;
  }

  if(options?.filters) {
    options.filters.forEach((value, key) => {
      fromObject[`filter[${key}]`] = value.join();
    });
  }

  return new HttpParams({ fromObject });
}
